'use client';

import type { FC } from 'react';
import { createPortal } from 'react-dom';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useScreenLayout } from '../../screen/useScreenLayout';
import { getPortalRoot } from '../portalRoot';
import translations from './SupportChat.translations';

interface SupportLauncherProps {
  unreadCount: number | undefined;
  onOpen: () => void;
}

export const SupportLauncher: FC<SupportLauncherProps> = ({ unreadCount, onOpen }) => {
  const { t } = useLocalTranslation(translations);
  const { tabBar, fixedBottom } = useScreenLayout();

  // The tab bar already carries the bottom safe area in its measured
  // height; only clear the inset ourselves when no bar is drawn.
  const occupied = (tabBar.visible ? tabBar.height : 0) + fixedBottom.height;
  const bottom =
    occupied > 0 ? `${occupied + 12}px` : 'calc(16px + var(--tg-viewport-safe-area-inset-bottom, 0px))';

  return createPortal(
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('launcher')}
      style={{ bottom }}
      className="fixed right-4 z-[900] flex h-14 w-14 items-center justify-center rounded-full bg-accent-blue text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)] active:opacity-80"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M21 11.5a8.38 8.38 0 0 1-9 8.34 9.1 9.1 0 0 1-3.3-.62L3 21l1.9-5.2A8.2 8.2 0 0 1 4 11.5 8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {unreadCount != null && unreadCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-red px-1 text-[11px] font-bold tabular-nums text-white">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>,
    getPortalRoot(),
  );
};
